import { useEffect } from 'react';
import { Progress } from '@backstage/core-components';
import {
  DevHubAlert,
  DevHubGrid,
  DevHubLink,
  DevHubChip,
} from '@devxdiscover/devhub-ui';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import { JiraStory, useJiraStoryRequest } from '../../services/jira';
import { WorkBoxItem } from './WorkboxItem';

type WorkTableProps = {
  jira: JiraStory[];
  error: boolean;
  loading: boolean;
  expanded?: boolean;
};

const WorkTable = ({ jira, loading, error, expanded }: WorkTableProps) => {
  // Load PrimeReact theme based on DevHub theme setting
  useEffect(() => {
    if (localStorage.getItem('theme') === 'devhub-dark') {
      import('primereact/resources/themes/md-dark-indigo/theme.css');
    } else {
      import('primereact/resources/themes/md-light-indigo/theme.css');
    }
  }, []);

  const statusColors: { [key: string]: any } = {
    Accepted: 'purple',
    Approved: 'green',
    Backlog: 'red',
    Blocked: 'red',
    Buildbroken: 'red',
    Building: 'blueGreen',
    Cancelled: 'black',
    Completed: 'green',
    Closed: 'green',
    Default: 'blue',
    Defined: 'blue',
    Done: 'green',
    Draft: 'gray',
    InCodeReview: 'purpleBlue',
    InProgress: 'blueGreen',
    InReview: 'purpleBlue',
    Open: 'blue',
    Published: 'green',
    Rejected: 'red',
    Reopened: 'blue',
    Resolved: 'green',
    SecondReview: 'purple',
    SelectedforDevelopment: 'blue',
    TechnologyElaboration: 'blue',
    ToDo: 'blue',
    Underreview: 'purpleBlue',
    awaitingreview: 'green',
  };

  // Template functions for DataTable columns
  const ticketIdTemplate = (rowData: JiraStory) => {
    const link = rowData.jiraInstance
      ? `${rowData.jiraInstance}/browse/${rowData.ticketId}`
      : undefined;

    return link ? (
      <DevHubLink href={link} target="_blank">
        {rowData.ticketId}
      </DevHubLink>
    ) : (
      rowData.ticketId
    );
  };

  const statusTemplate = (rowData: JiraStory) => {
    const formattedStatus = rowData.issueStatus
      ?.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const colorKey = (rowData.issueStatus ?? 'Default').replace(/\s/g, '');
    const chipColor = statusColors[colorKey] || statusColors.Default;

    return (
      <DevHubChip
        label={formattedStatus || 'Unknown'}
        chipColor={chipColor}
        chipSize="small"
      />
    );
  };

  const dateTemplate = (rowData: JiraStory) => {
    return new Date(rowData.created).toLocaleDateString();
  };

  const summaryTemplate = (rowData: JiraStory) => {
    return rowData.summary || '';
  };

  return (
    <>
      {loading && <Progress />}
      {!loading && error && (
        <DevHubAlert severity="error">Error loading Jira tickets.</DevHubAlert>
      )}
      {!loading && !error && jira?.length === 0 && (
        <DevHubAlert severity="info">No Jira tickets found.</DevHubAlert>
      )}
      {!loading && !error && expanded && jira?.length > 0 && (
        <DataTable
          value={jira}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          tableStyle={{ minWidth: '50rem' }}
          sortField="created"
          sortOrder={-1}
          emptyMessage="No Jira tickets found."
        >
          <Column
            field="ticketId"
            header="Ticket ID"
            body={ticketIdTemplate}
            sortable
            style={{ width: '12rem' }}
          />
          <Column
            field="issueStatus"
            header="Status"
            body={statusTemplate}
            sortable
            style={{ width: '10rem' }}
          />
          <Column
            field="created"
            header="Created Date"
            body={dateTemplate}
            sortable
            style={{ width: '10rem' }}
          />
          <Column
            field="summary"
            header="Summary"
            body={summaryTemplate}
            sortable
          />
        </DataTable>
      )}
      {!loading && !error && !expanded && (
        <DevHubGrid container spacing={2} xs={12}>
          {jira?.length > 0 &&
            jira.slice(0, 6).map(story => (
              <DevHubGrid key={story?.ticketId} item xs={12} lg={4}>
                <WorkBoxItem
                  chipLabel={story?.issueStatus
                    ?.split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                  chipColor={
                    statusColors[story?.issueStatus.replace(/\s/g, '')] ||
                    statusColors.Default
                  }
                  link={
                    story?.jiraInstance
                      ? `${story?.jiraInstance}/browse/${story?.ticketId}`
                      : undefined
                  }
                  title={story?.ticketId}
                  date={story?.created}
                  body={story?.summary}
                />
              </DevHubGrid>
            ))}
        </DevHubGrid>
      )}
    </>
  );
};

/**
 * A component that displays the user's work items from Rally and JIRA.
 * @param expanded - Whether the component should be expanded by default.
 * @constructor
 */
export const MyTickets = ({ expanded = false }: { expanded?: boolean }) => {
  const {
    value: jiraValue,
    loading: jiraIsLoading,
    error: jiraHasError,
  } = useJiraStoryRequest();

  return (
    <WorkTable
      jira={jiraValue || []}
      error={Boolean(jiraHasError)}
      loading={jiraIsLoading}
      expanded={expanded}
    />
  );
};
